/**
 * Strategy Doc — generates a Word-compatible HTML document for a strategy
 * that carries a detailTemplate, plus helpers to download it and compose
 * an email handing it off to a client.
 *
 * Output is HTML wrapped in a Word-compatible preamble, named .doc and
 * served with the Word MIME type. Word opens this natively and renders
 * cleanly. Avoids pulling in the docx npm package (~500KB).
 */

const fmt = (n) =>
  isFinite(n) && n !== 0 ? Math.round(n).toLocaleString("en-US") : "0";

export function hasStrategyDoc(strategy) {
  return Boolean(
    strategy && (strategy.detailTemplate || strategy.clientBrief),
  );
}

// Build the 10-year forecast rows from the template assumptions
function buildForecastRows(f) {
  if (!f) return [];
  const rows = [];
  let balance = 0;
  let cumulativeSavings = 0;
  for (let y = 1; y <= f.years; y++) {
    const investmentReturn = y === 1 ? 0 : balance * f.annualReturn;
    const programs = investmentReturn * f.programPct;
    const retained = investmentReturn * f.retainPct;
    const excise = investmentReturn * f.excisePct;
    balance = balance + f.annualContribution + retained - excise;
    cumulativeSavings += f.annualContribution * f.marginalRate;
    rows.push({
      year: y,
      contribution: f.annualContribution,
      investmentReturn,
      programs,
      retained,
      excise,
      balance,
      cumulativeSavings,
      isMilestone: !!f.milestones?.[y],
      milestoneNote: f.milestones?.[y] || "",
    });
  }
  return rows;
}

function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Build a Word-compatible HTML doc string for a strategy.
 *
 * @param {object} strategy   The full strategy object (must have detailTemplate).
 * @param {object} ctx        Personalization context:
 *   { clientName, firmName, taxYear, preparedDate, contributionAmount }
 */
export function buildStrategyDocHtml(strategy, ctx = {}) {
  const t = strategy?.detailTemplate;
  if (!t) return "";

  const clientName = escapeHtml(ctx.clientName || "Valued Client");
  const firmName = escapeHtml(ctx.firmName || "Linked Accounting LLP");
  const preparedDate = escapeHtml(
    ctx.preparedDate ||
      new Date().toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }),
  );
  const taxYear = escapeHtml(ctx.taxYear || new Date().getFullYear());

  const forecastRows = buildForecastRows(t.forecast);

  const ICON_CHECK = "✓";
  const ICON_WARN = "⚠";

  const html = `<!DOCTYPE html>
<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
<head>
<meta charset="utf-8">
<title>${escapeHtml(strategy.name)} — Strategy Brief</title>
<!--[if gte mso 9]>
<xml>
  <w:WordDocument>
    <w:View>Print</w:View>
    <w:Zoom>100</w:Zoom>
    <w:DoNotOptimizeForBrowser/>
  </w:WordDocument>
</xml>
<![endif]-->
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; color: #222; line-height: 1.45; }
  h1 { font-size: 22pt; color: #0c4a6e; margin: 0 0 4pt 0; }
  h2 { font-size: 14pt; color: #0c4a6e; border-bottom: 1.5pt solid #38BDF8; padding-bottom: 3pt; margin-top: 18pt; margin-bottom: 8pt; text-transform: uppercase; letter-spacing: 1.2pt; }
  h3 { font-size: 11pt; color: #1c1917; margin: 10pt 0 4pt 0; }
  .letterhead { border-bottom: 2pt solid #38BDF8; padding-bottom: 8pt; margin-bottom: 18pt; }
  .letterhead .firm { font-size: 16pt; font-weight: bold; color: #0c0c0c; }
  .letterhead .tagline { font-size: 9pt; letter-spacing: 1.5pt; color: #666; text-transform: uppercase; }
  .letterhead .meta { float: right; text-align: right; font-size: 9pt; color: #666; }
  .audit { display: inline-block; background: #ecfeff; border: 1pt solid #38BDF8; padding: 6pt 10pt; margin: 6pt 0 12pt 0; }
  .audit .rating { font-size: 18pt; font-weight: bold; color: #0c4a6e; }
  .audit .label { font-size: 9pt; color: #444; text-transform: uppercase; letter-spacing: 1pt; }
  .section-intro { font-style: italic; color: #444; margin-bottom: 10pt; }
  .step { background: #f8fafc; border-left: 3pt solid #38BDF8; padding: 6pt 10pt; margin: 5pt 0; }
  .step .num { display: inline-block; background: #38BDF8; color: white; width: 18pt; height: 18pt; line-height: 18pt; text-align: center; font-weight: bold; border-radius: 9pt; margin-right: 8pt; }
  .step .title { font-weight: bold; color: #0c4a6e; }
  table.forecast { border-collapse: collapse; width: 100%; margin: 8pt 0; font-size: 9pt; }
  table.forecast th { background: #0c0c0c; color: #38BDF8; padding: 6pt 4pt; text-align: right; font-weight: bold; }
  table.forecast th:first-child { text-align: left; }
  table.forecast td { padding: 4pt; border-bottom: 0.5pt solid #ddd; text-align: right; font-family: 'Consolas', monospace; }
  table.forecast td:first-child { text-align: left; font-family: Calibri, sans-serif; font-weight: bold; }
  table.forecast tr.milestone td { background: #fffbeb; border-top: 1pt solid #f59e0b; }
  .milestone-note { background: #fffbeb; border-left: 3pt solid #f59e0b; padding: 6pt 10pt; margin: 5pt 0; font-size: 10pt; }
  .faq { margin: 6pt 0; }
  .faq .q { font-weight: bold; color: #0c4a6e; margin-bottom: 2pt; }
  .faq .a { margin-left: 0; color: #333; }
  .caution { background: #fef2f2; border-left: 3pt solid #dc2626; padding: 6pt 10pt; margin: 5pt 0; }
  .caution .title { font-weight: bold; color: #991b1b; }
  .next-step { background: #ecfdf5; border-left: 3pt solid #10b981; padding: 6pt 10pt; margin: 4pt 0; }
  .disclaimer { font-size: 8pt; color: #666; border-top: 1pt solid #ccc; padding-top: 8pt; margin-top: 18pt; line-height: 1.35; }
  .clearfix:after { content: ""; display: table; clear: both; }
</style>
</head>
<body>

<div class="letterhead clearfix">
  <div class="meta">
    Prepared ${preparedDate}<br>
    Tax Year ${taxYear}<br>
    For: <strong>${clientName}</strong>
  </div>
  <div class="firm">${firmName}</div>
  <div class="tagline">Comprehensive Tax Planning</div>
</div>

<h1>${escapeHtml(strategy.name)}</h1>
<p style="font-size:12pt; color:#444; margin-top:0;">${escapeHtml(strategy.shortDescription || "")}</p>

${
  t.auditConfidence
    ? `<div class="audit">
  <div class="label">IRS Audit Confidence</div>
  <div class="rating">${escapeHtml(t.auditConfidence.percent)} — ${escapeHtml(t.auditConfidence.label)}</div>
</div>`
    : ""
}

${
  t.idealClient
    ? `<h2>Ideal Tax Client</h2>
<p>${escapeHtml(t.idealClient)}</p>`
    : ""
}

${
  t.overview
    ? `<h2>Strategy Overview</h2>
${(Array.isArray(t.overview) ? t.overview : [t.overview]).map((p) => `<p>${escapeHtml(p)}</p>`).join("\n")}`
    : ""
}

${
  Array.isArray(t.flow) && t.flow.length > 0
    ? `<h2>Flow of Funds</h2>
${t.flow
  .map(
    (f) => `<div class="step">
  <span class="num">${escapeHtml(f.step)}</span>
  <span class="title">${escapeHtml(f.title)}</span>
  <div style="margin-top:3pt;">${escapeHtml(f.body)}</div>
</div>`,
  )
  .join("\n")}`
    : ""
}

${
  forecastRows.length > 0
    ? `<h2>Financial Forecast — ${t.forecast.years}-Year Horizon</h2>
${t.forecast.intro ? `<p class="section-intro">${escapeHtml(t.forecast.intro)}</p>` : ""}
<table class="forecast">
  <thead>
    <tr>
      <th>Year</th>
      <th>Contribution</th>
      <th>Invest. Return</th>
      <th>To Programs</th>
      <th>Retained</th>
      <th>Excise</th>
      <th>Year-End Balance</th>
      <th>Cumulative Tax Savings</th>
    </tr>
  </thead>
  <tbody>
${forecastRows
  .map(
    (r) => `    <tr${r.isMilestone ? ' class="milestone"' : ""}>
      <td>Year ${r.year}${r.isMilestone ? " ★" : ""}</td>
      <td>$${fmt(r.contribution)}</td>
      <td>$${fmt(r.investmentReturn)}</td>
      <td>$${fmt(r.programs)}</td>
      <td>$${fmt(r.retained)}</td>
      <td>$${fmt(r.excise)}</td>
      <td>$${fmt(r.balance)}</td>
      <td>$${fmt(r.cumulativeSavings)}</td>
    </tr>`,
  )
  .join("\n")}
  </tbody>
</table>
${Object.entries(t.forecast.milestones || {})
  .map(
    ([yr, note]) =>
      `<div class="milestone-note"><strong>★ Year ${escapeHtml(yr)} milestone:</strong> ${escapeHtml(note)}</div>`,
  )
  .join("\n")}
${t.forecast.note ? `<p class="section-intro" style="font-size:9pt;">${escapeHtml(t.forecast.note)}</p>` : ""}`
    : ""
}

${
  Array.isArray(t.faqs) && t.faqs.length > 0
    ? `<h2>Frequently Asked Questions</h2>
${t.faqs
  .map(
    (f) => `<div class="faq">
  <div class="q">${escapeHtml(f.q)}</div>
  <div class="a">${escapeHtml(f.a)}</div>
</div>`,
  )
  .join("\n")}`
    : ""
}

${
  Array.isArray(t.cautions) && t.cautions.length > 0
    ? `<h2>${ICON_WARN} Critical Considerations</h2>
${t.cautions
  .map(
    (c) => `<div class="caution">
  <span class="title">${escapeHtml(c.title)}:</span> ${escapeHtml(c.body)}
</div>`,
  )
  .join("\n")}`
    : ""
}

${
  Array.isArray(t.nextSteps) && t.nextSteps.length > 0
    ? `<h2>Recommended Next Steps</h2>
${t.nextSteps
  .map((s) => `<div class="next-step">${ICON_CHECK} ${escapeHtml(s)}</div>`)
  .join("\n")}`
    : ""
}

${
  t.disclaimer
    ? `<div class="disclaimer">${escapeHtml(t.disclaimer)}</div>`
    : ""
}

</body>
</html>`;
  return html;
}

/**
 * Trigger a browser download of the strategy doc as a .doc file.
 * Returns the suggested filename so callers can reference it.
 */
export function downloadStrategyDoc(strategy, ctx = {}) {
  if (typeof window === "undefined") return null;
  const html = buildStrategyDocHtml(strategy, ctx);
  // Use the Word HTML preamble + MS Word MIME type so Word opens it natively
  const preamble =
    "<!DOCTYPE html><html xmlns:o='urn:schemas-microsoft-com:office:office' " +
    "xmlns:w='urn:schemas-microsoft-com:office:word' " +
    "xmlns='http://www.w3.org/TR/REC-html40'>";
  // The HTML we built already has its own <!DOCTYPE html>, so use as-is.
  const blob = new Blob([html], { type: "application/msword;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const slug = (strategy.name || "strategy")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const clientSlug = (ctx.clientName || "client")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  const filename = `${slug}-${clientSlug}.doc`;
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke the URL after a delay so the download completes
  setTimeout(() => URL.revokeObjectURL(url), 5000);
  return filename;
}

/**
 * Compose a mailto: URL for sending a strategy doc to a client.
 * Note: mailto: cannot attach files — the caller must download the doc
 * separately and prompt the user to attach.
 */
export function buildStrategyMailto(strategy, ctx = {}) {
  const to = ctx.clientEmail || "";
  const clientName = ctx.clientName || "Client";
  const firmName = ctx.firmName || "Linked Accounting LLP";
  const senderName = ctx.senderName || "";
  const filename = ctx.attachmentFilename || `${strategy.name}.doc`;
  const subject = `Tax Strategy: ${strategy.name}`;
  const body = [
    `Hi ${clientName.split(" ")[0] || "there"},`,
    "",
    `Attached is a brief on the ${strategy.name} tax strategy we discussed. It walks through:`,
    "",
    "  • Who the strategy fits and why",
    "  • The flow of funds and tax mechanics",
    "  • A multi-year financial forecast",
    "  • Frequently asked questions",
    "  • Key cautions and compliance considerations",
    "  • Recommended next steps if you'd like to move forward",
    "",
    `Please take a look when you have a moment, and let me know if any questions come up. Happy to schedule a call to walk through it.`,
    "",
    "Best,",
    senderName || firmName,
    "",
    `(Attachment to add: ${filename})`,
  ].join("\n");

  const qs =
    `subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  return `mailto:${encodeURIComponent(to)}?${qs}`;
}

export default {
  hasStrategyDoc,
  buildStrategyDocHtml,
  downloadStrategyDoc,
  buildStrategyMailto,
};
